import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'auth';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Logger } from 'shared';
import { TranslateService } from './services/translate.service';

/**
 * Languages written right to left. Matched on the base language, so a regional
 * variant ('ar-EG') is recognised too. Keep in sync with the backend's
 * config.i18n.available_locales — a new RTL language belongs in both.
 */
export const RTL_LANGUAGES: readonly string[] = ['ar'];

export function isRtlLanguage(lang: string | null | undefined): boolean {
  if (!lang) return false;
  return RTL_LANGUAGES.includes(lang.toLowerCase().split('-')[0]);
}

@Injectable({
  providedIn: 'root'
})
export class I18nService {
  protected logger = new Logger('I18nService');
  private timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Generic superset, used only until the backend's per-domain `languages`
  // setting arrives (see createAppConfig). Keep in sync with
  // config.i18n.available_locales and User::SUPPORTED_LANGUAGES.
  private supportedLanguages = ['en', 'de', 'es', 'es-MX', 'fr', 'pt', 'it', 'ru', 'cs', 'sk', 'ar'];
  private defaultLang = 'en';
  private currentLanguageSubject = new BehaviorSubject<string>(this.getLanguage());
  public currentLanguage$: Observable<string> = this.currentLanguageSubject.asObservable();

  constructor(
    private authService: AuthService,
    private translateService: TranslateService,
    private router: Router,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.authService.getUser$().pipe(take(1)).subscribe(user => {
      const lang = user?.language || localStorage.getItem('language') || this.locale;
      this.setLanguage(lang, false); // no navigation on init
    });

    // The document's own language and writing direction follow the active language:
    // nothing else sets `dir`, and without it an RTL language (Arabic) would render
    // left to right. `libs/credo/src/scss/_rtl.scss` hangs off the same attribute.
    this.translateService.langChanges.subscribe(lang => {
      this.currentLanguageSubject.next(lang);
      if (typeof document !== 'undefined') {
        document.documentElement.lang = lang;
        document.documentElement.dir = isRtlLanguage(lang) ? 'rtl' : 'ltr';
      }
    });
  }

  setLanguage(lang: string, navigate: boolean = true): void {
    // Constrain to the app's configured available languages when known, otherwise
    // to the generic supported set. This prevents activating a language the app
    // doesn't offer (e.g. the browser-locale 'de' on an English-only instance),
    // which would have no translations and render raw keys.
    const availableLangs = this.translateService.getAvailableLangs();
    const allowedLangs = availableLangs?.length ? availableLangs : this.supportedLanguages;
    if (!allowedLangs.includes(lang)) {
      const fallbackLang = allowedLangs.includes(this.defaultLang) ? this.defaultLang : allowedLangs[0];
      this.logger.warn(`Unsupported language: ${lang}. Falling back to ${fallbackLang}`);
      lang = fallbackLang;
    }

    // if (lang === this.getLanguage()) {
    if (lang === this.translateService.getActiveLang()) {
      return; // no change required
    }

    this.translateService.setActiveLang(lang);
    localStorage.setItem('language', lang);
    // this.currentLanguageSubject.next(lang); // Update the observable

    if (navigate) {
      const currentUrl = this.router.url.split('/').filter(segment => segment);
      if (currentUrl.length > 0 && this.supportedLanguages.includes(currentUrl[0])) {
        currentUrl[0] = lang;     // replace language
      } else {
        currentUrl.unshift(lang); // prepend if no lang
      }
     
      // truncate at 'path:' segment
      const pathIndex = currentUrl.findIndex(segment => segment.startsWith('path:'));
      if (pathIndex !== -1) {
        currentUrl.splice(pathIndex); // remove 'path:' and everything after
      }

      const newUrl = '/' + currentUrl.join('/');
      this.router.navigateByUrl(newUrl);
    }
  }

  getLanguage(): string {
    let lang: string | undefined = this.translateService.getActiveLang();
    if (!lang) {
      const availableLangs = this.translateService.getAvailableLangs();
      if (availableLangs?.length) {
        const fallbackLang = localStorage.getItem('language') || this.locale;
        lang = availableLangs.find(lang => lang == fallbackLang);
      }
    }
    return lang || 'en';
  }

  /**
   * Native name of a language, e.g. 'de' -> 'Deutsch'.
   *
   * @param code Language to name; defaults to the currently active one. Passing a code lets a
   *             language picker label every option it offers, not just the active language.
   */
  getLanguageName(code?: string): string {
    //TODO: lang name should be taken from labels
    const lang = (code ?? this.getLanguage()).toLowerCase();
    const languageNames: Record<string, string> = {
      'en': 'English',
      'de': 'Deutsch',
      'es': 'Español',
      'es-mx': 'Español (México)',
      'fr': 'Français',
      'pt': 'Português',
      'it': 'Italiano',
      'ru': 'Русский',
      'cs': 'Čeština',
      'sk': 'Slovenčina',
      'ar': 'العربية'
    };

    // Region-specific codes are matched first (es-MX is a language of its own to
    // the reader, even though it inherits its translations from es), then by
    // prefix so an unlisted regional variant (e.g. 'en-US') still resolves.
    const langCode = lang.split('-')[0];
    return languageNames[lang] || languageNames[langCode] || lang;
  }

  /** True when the active language is written right to left. */
  isRtl(): boolean {
    return isRtlLanguage(this.getLanguage());
  }

  getTimezone(): string {
    return this.timezone;
  }
}